import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";
import type {
    StearnsAnchorBlock as AnchorBlockValue,
    StearnsFAQsBlock as FAQsBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { StearnsAnchorBlock } from "../../blocks/StearnsAnchorBlock";
import { StearnsFAQsBlock } from "../../blocks/StearnsFAQsBlock";

/**
 * `text` / `table` / `typed_table` all serialize to a server-rendered HTML
 * string (`ClientRenderingBarrier` -- the thelab-ui `RichTextBlock` /
 * `TableBlock` barrier and the stearns-local `TypedTableBlock` barrier in
 * `server/cms/pages/offer_detail_page.py`). They are rendered raw, mirroring the
 * legacy `{{ page.content }}` per-block server output.
 */
const RawHTML = (
    block: { value: string | null },
    className?: string,
): React.JSX.Element | null =>
    block.value ? (
        <div
            className={className}
            dangerouslySetInnerHTML={{ __html: block.value }}
        />
    ) : null;

/**
 * `OfferDetailContent` StreamField dispatch map (5 keys):
 *   - `text` / `table` / `typed_table` -> HTML string (server-rendered)
 *   - `anchor_link` -> StearnsAnchorBlock (ported, direct `value`)
 *   - `FAQs` -> StearnsFAQsBlock (ported, direct `value`)
 */
export const offerDetailContentComponentsMap = {
    text: RawHTML,
    // Legacy `{{ page.content }}` rendered each block inside Wagtail's
    // `<div class="block-{type}">` wrapper. `offer-details.scss` relies on that
    // for the `table` block only: `.offer-details-page .block-table` adds
    // `overflow-x: auto` for horizontal scrolling. Carry that class on the
    // table block's own div (single div, matching the legacy markup).
    table: (block: { value: string | null }) => RawHTML(block, "block-table"),
    typed_table: RawHTML,
    anchor_link: (block: { value: Immutable<AnchorBlockValue> }) => (
        <StearnsAnchorBlock value={block.value} />
    ),
    FAQs: (block: { value: Immutable<FAQsBlockValue> }) => (
        <StearnsFAQsBlock value={block.value} />
    ),
} as const;

export const OfferDetailPageBody = (
    props: Immutable<Pick<templates.OfferDetailPageTemplate, "page">>,
): React.JSX.Element => {
    // `content` is a required StreamField (no `null=True`), so it is always
    // present; an empty stream renders nothing.
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.content.value}
            components={offerDetailContentComponentsMap}
        />
    );
};
